import { Search } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useI18n } from '../../../../application/language/useI18n';
import { Colors } from '../../../design/color/Colors';
import { observer } from 'mobx-react';
import React, { useMemo, useState } from 'react';
import { IMainLayoutDomainStore } from '../../../layout/main/store/domain/IMainLayoutDomainStore';

import { PageLoader } from '../../../design/page/PageLoader';
import { useDeferredCallback } from '../../../shared/hooks/useDeferredCallback';
import { useProgressiveRender } from '../../../shared/hooks/useProgressiveRender';
import { SystemsIcon } from './icons/SystemsIcon';
import { GroupCardComponentHeader } from './parts/GroupCardComponent/GroupCardComponentHeader';
import { SystemsList } from './parts/SystemsList';
import { IUserUnitCardProperties, UserUnitCard } from './parts/UserUnitCard';
import { UserUnitListCard } from './parts/UserUnitListCard';
import { IPassingListPageDomain } from './store/IPassingListPageDomain';
import { PassingListPageDomain } from './store/PassingListPageDomain';

export enum FilterValues {
  All = 'all',
  Important = 'important',
  Unimportant = 'unimportant',
}

export interface IPassingListPage {
  layoutDomain: IMainLayoutDomainStore;
  passingListPageDomain?: IPassingListPageDomain;
}

const createIsPassedProp = (value: unknown): IUserUnitCardProperties['isPassed'] =>
  typeof value === 'boolean' ? value : null;

const createStatePassingListPageDomain = ({ passingListPageDomain, layoutDomain }: IPassingListPage) =>
  passingListPageDomain ?? new PassingListPageDomain(layoutDomain);

type FlatItem =
  | { type: 'header'; groupName: string; count: number; passingPercent: number }
  | { type: 'card'; unit: any; unitResult: any; isPassed: boolean | null; status: 'important' | 'unimportant' };

export const PassingListPage = observer(({ layoutDomain, passingListPageDomain }: IPassingListPage) => {
  const [domain] = useState<IPassingListPageDomain>(
    createStatePassingListPageDomain({ layoutDomain, passingListPageDomain }),
  );
  const [filter, setFilter] = useState<FilterValues>(FilterValues.All);
  const i18n = useI18n();

  useDeferredCallback(() => {
    domain.loadData();
  }, [domain]);

  const unitResultMap = useMemo(() => {
    const map = new Map<string, typeof domain.ui.passingReslutList.list[0]>();
    domain.ui.passingReslutList.list.forEach((r) => map.set(r.unitId, r));
    return map;
  }, [domain.ui.passingReslutList.list]);

  const flatItems = useMemo(() => {
    const items: FlatItem[] = [];
    domain.ui.filtredGroupedUnits.list.forEach((group) => {
      const important = group.importantUnitList;
      const unimportant = group.unimportantUnitList;
      const allUnits = [...important, ...unimportant];

      const shouldRender =
        (filter === FilterValues.All && allUnits.length) ||
        (filter === FilterValues.Important && important.length) ||
        (filter === FilterValues.Unimportant && unimportant.length);

      if (!shouldRender) return;

      const unitsToRender =
        filter === FilterValues.All ? allUnits : filter === FilterValues.Important ? important : unimportant;

      items.push({
        type: 'header',
        groupName: group.groupName,
        count: unitsToRender.length,
        passingPercent: group.passingPercent,
      });

      unitsToRender.forEach((unit) => {
        const unitResult = unitResultMap.get(unit.id);
        items.push({
          type: 'card',
          unit,
          unitResult,
          isPassed: createIsPassedProp(unitResult?.isPassed),
          status: filter === FilterValues.All
            ? important.includes(unit) ? 'important' : 'unimportant'
            : filter as any,
        });
      });
    });
    return items;
  }, [domain.ui.filtredGroupedUnits.list, filter, unitResultMap]);

  const { visibleItems, hasMore, sentinelRef, scrollToId } = useProgressiveRender(flatItems);

  const hasUnits = flatItems.length > 0;

  if (domain.ui.isLoading.value) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <PageLoader />
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Accordion elevation={0} sx={{ borderBottom: `1px solid ${Colors.border}`, padding: '4px 0px' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative', left: '-16px' }}>
            <SystemsIcon />
            {i18n.translate('passingListPage.systems')}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <SystemsList systems={domain.ui.applicationsWithUnits.list} />
        </AccordionDetails>
      </Accordion>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: '24px',
          margin: '0px 0px 24px 0px ',
          paddingBottom: '24px',
          borderBottom: `1px solid ${Colors.border}`,
        }}
      >
        <Typography variant="h4" sx={{ marginTop: '16px' }}>
          {i18n.translate('passingListPage.skillsMap')}
        </Typography>

        <Box sx={{ display: 'flex', gap: '24px', alignItems: 'flex-end' }}>
          <TextField
            size="small"
            value={domain.ui.searchText.value}
            onChange={(e) => domain.handleSearchByGroupsAndName(e.target.value)}
            label={i18n.translate('passingListPage.unitSearch')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <Search />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControl>
            <InputLabel>{i18n.translate('passingListPage.showUnits')}</InputLabel>
            <Select
              sx={{ width: '160px' }}
              variant="outlined"
              size="small"
              label={i18n.translate('passingListPage.showUnits')}
              value={filter}
              onChange={(e) => setFilter(e.target.value as FilterValues)}
            >
              <MenuItem value={FilterValues.All}>{i18n.translate('passingListPage.unitsFilter.all')}</MenuItem>
              <MenuItem value={FilterValues.Important}>
                {i18n.translate('passingListPage.unitsFilter.important')}
              </MenuItem>
              <MenuItem value={FilterValues.Unimportant}>
                {i18n.translate('passingListPage.unitsFilter.unimportant')}
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', gap: '16px', overflow: 'hidden' }}>
        <Box sx={{ flex: '0 0 28%', display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
          {domain.ui.filtredGroupedUnits.list.map((group) => {
            const show =
              filter === FilterValues.All ||
              (filter === FilterValues.Important && group.importantUnitList.length) ||
              (filter === FilterValues.Unimportant && group.unimportantUnitList.length);
            return show ? <UserUnitListCard key={group.groupName} group={group} filter={filter} scrollToId={scrollToId} /> : null;
          })}
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflowY: 'auto',
            height: 'calc(100vh - 340px)',
          }}
        >
          {!hasUnits && (
            <Box
              sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'text.secondary' }}
            >
              <Typography variant="h5">{i18n.translate('passingListPage.noUnits')}</Typography>
            </Box>
          )}

          {visibleItems.map((item, index) => {
            if (item.type === 'header') {
              return (
                <GroupCardComponentHeader
                  key={`header-${item.groupName}`}
                  groupName={item.groupName}
                  count={item.count}
                  passingPercent={item.passingPercent}
                />
              );
            }
            return (
              <UserUnitCard
                key={item.unit.id}
                unit={item.unit}
                action={domain.redirect}
                unitResult={item.unitResult}
                isPassed={item.isPassed}
                status={item.status}
              />
            );
          })}
          {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
        </Box>
      </Box>
    </Box>
  );
});
